import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Lock, Volume2, Headphones } from 'lucide-react';
import { ScenarioData } from './ScenarioWizard';
interface Step3PersonalizationProps {
  scenarioData: ScenarioData;
  onUpdate: (data: Partial<ScenarioData>) => void;
  onNext: () => void;
  onBack: () => void;
}
export const Step3Personalization: React.FC<Step3PersonalizationProps> = ({
  scenarioData,
  onUpdate,
  onNext,
  onBack
}) => {
  const [showPremiumInfo, setShowPremiumInfo] = useState(false);
  const [activeTab, setActiveTab] = useState<'basics' | 'advanced'>('basics');
  const [showActsDropdown, setShowActsDropdown] = useState(false);
  // Extended categories based on screenshots
  const locations = [{
    id: 'bedroom',
    label: 'Bedroom',
    premium: false
  }, {
    id: 'hotel',
    label: 'Luxury Hotel',
    premium: false
  }, {
    id: 'beach',
    label: 'Private Beach',
    premium: false
  }, {
    id: 'office',
    label: 'Office After Hours',
    premium: false
  }, {
    id: 'yacht',
    label: 'Private Yacht',
    premium: true
  }, {
    id: 'cabin',
    label: 'Mountain Cabin',
    premium: true
  }, {
    id: 'bath_house',
    label: 'Bath House',
    premium: false
  }, {
    id: 'bar',
    label: 'Bar',
    premium: false
  }, {
    id: 'bdsm_dungeon',
    label: 'BDSM Dungeon',
    premium: true
  }];
  const outfits = [{
    id: 'lingerie',
    label: 'Lingerie',
    premium: false
  }, {
    id: 'bikini',
    label: 'Bikini',
    premium: false
  }, {
    id: 'dress',
    label: 'Cocktail Dress',
    premium: false
  }, {
    id: 'uniform',
    label: 'Sexy Uniform',
    premium: false
  }, {
    id: 'leather',
    label: 'Leather Outfit',
    premium: true
  }, {
    id: 'cosplay',
    label: 'Cosplay Costume',
    premium: true
  }, {
    id: 'business',
    label: 'Business Attire',
    premium: false
  }, {
    id: 'naked',
    label: 'Naked',
    premium: false
  }];
  const tones = [{
    id: 'flirty',
    label: 'Flirty',
    premium: false
  }, {
    id: 'romantic',
    label: 'Romantic',
    premium: false
  }, {
    id: 'kinky',
    label: 'Kinky',
    premium: false
  }, {
    id: 'dominant',
    label: 'Dominant',
    premium: true
  }, {
    id: 'submissive',
    label: 'Submissive',
    premium: true
  }, {
    id: 'playful',
    label: 'Playful',
    premium: false
  }, {
    id: 'teasing',
    label: 'Teasing',
    premium: false
  }];
  const perspectives = [{
    id: 'pov',
    label: 'First Person (POV)',
    premium: false
  }, {
    id: 'third_person',
    label: 'Third Person',
    premium: false
  }, {
    id: 'voyeur',
    label: 'Voyeur Perspective',
    premium: true
  }];
  // New categories based on screenshots
  const sexActs = ['Kissing', 'Oral', 'Anal', 'Vaginal', 'Dirty Talk', 'Roleplay', 'Massage', 'Striptease', 'Masturbation', 'Toys', 'Bondage', 'Spanking', 'Threesome', 'Voyeurism', 'Public'];
  const storyLengths = [{
    id: 'short',
    label: 'Short (5 min)',
    premium: false
  }, {
    id: 'medium',
    label: 'Medium (15 min)',
    premium: false
  }, {
    id: 'long',
    label: 'Long (30 min)',
    premium: true
  }];
  const voiceOptions = [{
    id: 'none',
    label: 'No Voice',
    premium: false
  }, {
    id: 'female',
    label: 'Female Voice',
    premium: true
  }, {
    id: 'male',
    label: 'Male Voice',
    premium: true
  }];
  const soundQuality = [{
    id: 'standard',
    label: 'Standard',
    premium: false
  }, {
    id: 'high',
    label: 'High Quality',
    premium: true
  }];
  return <div className="w-full" data-id="element-21939">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-21940">
        Personalize Your Scenario
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6" data-id="element-21941">
        Customize the details to make your fantasy perfect
      </p>

      {/* Tab Navigation */}
      <div className="flex mb-6 bg-gray-100 dark:bg-gray-800 rounded-lg p-1" data-id="element-21942">
        <button onClick={() => setActiveTab('basics')} className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'basics' ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-21943">
          Basic Details
        </button>
        <button onClick={() => setActiveTab('advanced')} className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'advanced' ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-21944">
          Advanced Options
        </button>
      </div>

      {activeTab === 'basics' ? <>
          {/* Location Selection */}
          <div className="mb-6" data-id="element-21945">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-21946">
              Location
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-id="element-21947">
              {locations.map(location => <button key={location.id} className={`relative px-3 py-2 rounded-lg text-sm ${scenarioData.location === location.id ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'} ${location.premium ? 'opacity-80' : ''}`} onClick={() => onUpdate({
            location: location.id
          })} data-id="element-21948">
                  {location.label}
                  {location.premium && <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-id="element-21949">
                      <Lock size={10} data-id="element-21950" />
                    </span>}
                </button>)}
            </div>
          </div>

          {/* Outfit Selection */}
          <div className="mb-6" data-id="element-21951">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-21952">
              Outfit
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-id="element-21953">
              {outfits.map(outfit => <button key={outfit.id} className={`relative px-3 py-2 rounded-lg text-sm ${scenarioData.outfit === outfit.id ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'} ${outfit.premium ? 'opacity-80' : ''}`} onClick={() => onUpdate({
            outfit: outfit.id
          })} data-id="element-21954">
                  {outfit.label}
                  {outfit.premium && <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-id="element-21955">
                      <Lock size={10} data-id="element-21956" />
                    </span>}
                </button>)}
            </div>
          </div>

          {/* Tone Selection */}
          <div className="mb-6" data-id="element-21957">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-21958">
              Tone
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-id="element-21959">
              {tones.map(tone => <button key={tone.id} className={`relative px-3 py-2 rounded-lg text-sm ${scenarioData.tone === tone.id ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'} ${tone.premium ? 'opacity-80' : ''}`} onClick={() => onUpdate({
            tone: tone.id
          })} data-id="element-21960">
                  {tone.label}
                  {tone.premium && <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-id="element-21961">
                      <Lock size={10} data-id="element-21962" />
                    </span>}
                </button>)}
            </div>
          </div>

          {/* Perspective Selection */}
          <div className="mb-6" data-id="element-21963">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-21964">
              Perspective
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-id="element-21965">
              {perspectives.map(perspective => <button key={perspective.id} className={`relative px-3 py-2 rounded-lg text-sm ${scenarioData.perspective === perspective.id ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'} ${perspective.premium ? 'opacity-80' : ''}`} onClick={() => onUpdate({
            perspective: perspective.id
          })} data-id="element-21966">
                  {perspective.label}
                  {perspective.premium && <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-id="element-21967">
                      <Lock size={10} data-id="element-21968" />
                    </span>}
                </button>)}
            </div>
          </div>
        </> : <>
          {/* Sex Acts Dropdown */}
          <div className="mb-6 relative" data-id="element-21969">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-21970">
              Sex Acts
            </label>
            <div className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg p-3 cursor-pointer flex justify-between items-center" onClick={() => setShowActsDropdown(!showActsDropdown)} data-id="element-21971">
              <span className="text-gray-700 dark:text-gray-300" data-id="element-21972">
                {scenarioData.sexActs ? scenarioData.sexActs.join(', ') : 'Select sex acts...'}
              </span>
              <ChevronRight className={`h-4 w-4 text-gray-500 transform transition-transform ${showActsDropdown ? 'rotate-90' : ''}`} data-id="element-21973" />
            </div>
            {showActsDropdown && <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-auto" data-id="element-21974">
                <div className="p-2 grid grid-cols-2 gap-1" data-id="element-21975">
                  {sexActs.map(act => <div key={act} className="flex items-center px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded" data-id="element-21976">
                      <input type="checkbox" id={`act-${act}`} className="mr-2 text-pink-500 focus:ring-pink-500" checked={scenarioData.sexActs?.includes(act) || false} onChange={e => {
                const currentActs = scenarioData.sexActs || [];
                if (e.target.checked) {
                  onUpdate({
                    sexActs: [...currentActs, act]
                  });
                } else {
                  onUpdate({
                    sexActs: currentActs.filter(a => a !== act)
                  });
                }
              }} data-id="element-21977" />
                      <label htmlFor={`act-${act}`} className="text-sm text-gray-700 dark:text-gray-300" data-id="element-21978">
                        {act}
                      </label>
                    </div>)}
                </div>
              </div>}
          </div>

          {/* Story Length */}
          <div className="mb-6" data-id="element-21979">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-21980">
              Story Length
            </label>
            <div className="grid grid-cols-3 gap-2" data-id="element-21981">
              {storyLengths.map(length => <button key={length.id} className={`relative px-3 py-2 rounded-lg text-sm ${scenarioData.storyLength === length.id ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'} ${length.premium ? 'opacity-80' : ''}`} onClick={() => onUpdate({
            storyLength: length.id
          })} data-id="element-21982">
                  {length.label}
                  {length.premium && <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-id="element-21983">
                      <Lock size={10} data-id="element-21984" />
                    </span>}
                </button>)}
            </div>
          </div>

          {/* Voice Options */}
          <div className="mb-6" data-id="element-21985">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-21986">
              Voice Narration
            </label>
            <div className="grid grid-cols-3 gap-2" data-id="element-21987">
              {voiceOptions.map(voice => <button key={voice.id} className={`relative px-3 py-2 rounded-lg text-sm ${scenarioData.voice === voice.id ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'} ${voice.premium ? 'opacity-80' : ''}`} onClick={() => onUpdate({
            voice: voice.id
          })} data-id="element-21988">
                  {voice.id !== 'none' && <Volume2 size={14} className="inline mr-1" data-id="element-21989" />}
                  {voice.label}
                  {voice.premium && <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-id="element-21990">
                      <Lock size={10} data-id="element-21991" />
                    </span>}
                </button>)}
            </div>
          </div>

          {/* Sound Quality - only show if voice is selected */}
          {scenarioData.voice && scenarioData.voice !== 'none' && <div className="mb-6" data-id="element-21992">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-21993">
                Sound Quality
              </label>
              <div className="grid grid-cols-2 gap-2" data-id="element-21994">
                {soundQuality.map(quality => <button key={quality.id} className={`relative px-3 py-2 rounded-lg text-sm ${scenarioData.soundQuality === quality.id ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'} ${quality.premium ? 'opacity-80' : ''}`} onClick={() => onUpdate({
            soundQuality: quality.id
          })} data-id="element-21995">
                    {quality.label}
                    {quality.premium && <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-id="element-21996">
                        <Lock size={10} data-id="element-21997" />
                      </span>}
                  </button>)}
              </div>
            </div>}

          {/* Add Sex Sounds Option */}
          <div className="mb-6" data-id="element-21998">
            <div className="flex items-center" data-id="element-21999">
              <input type="checkbox" id="addSexSounds" className="mr-2 text-pink-500 focus:ring-pink-500" checked={scenarioData.includeSexSounds || false} onChange={e => onUpdate({
            includeSexSounds: e.target.checked
          })} data-id="element-22000" />
              <label htmlFor="addSexSounds" className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center" data-id="element-22001">
                <Headphones size={16} className="mr-1" data-id="element-22002" />
                Include Sex Sounds
                <span className="ml-2 text-xs py-0.5 px-1.5 bg-pink-500 text-white rounded-full" data-id="element-22003">
                  Premium
                </span>
              </label>
            </div>
          </div>
        </>}

      {/* Additional Details - Always show */}
      <div className="mb-6" data-id="element-22004">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-22005">
          Additional Details (Optional)
        </label>
        <textarea value={scenarioData.additionalDetails} onChange={e => onUpdate({
        additionalDetails: e.target.value
      })} placeholder="Add any specific details you'd like included in your scenario..." className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-pink-500" rows={3} data-id="element-22006" />
      </div>

      {/* Premium Option Info */}
      <div className="mb-6" data-id="element-22007">
        <button onClick={() => setShowPremiumInfo(!showPremiumInfo)} className="text-sm text-pink-600 dark:text-pink-400 font-medium flex items-center" data-id="element-22008">
          <Lock size={14} className="mr-1" data-id="element-22009" />
          What are premium options?
        </button>
        {showPremiumInfo && <div className="mt-2 p-3 bg-pink-50 dark:bg-pink-900/20 rounded-lg border border-pink-200 dark:border-pink-800/30 text-sm text-gray-700 dark:text-gray-300" data-id="element-22010">
            Premium options are exclusive features available with Desire Gems
            💎. Upgrade your experience with more intimate and detailed
            scenarios!
          </div>}
      </div>

      <div className="flex justify-between mt-8" data-id="element-22011">
        <button onClick={onBack} className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg flex items-center gap-1 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" data-id="element-22012">
          <ChevronLeft size={16} data-id="element-22013" />
          <span data-id="element-22014">Back</span>
        </button>
        <button onClick={onNext} disabled={!scenarioData.location || !scenarioData.outfit} className={`px-4 py-2 rounded-lg flex items-center gap-1 transition-colors ${scenarioData.location && scenarioData.outfit ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:from-pink-600 hover:to-purple-600' : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'}`} data-id="element-22015">
          <span data-id="element-22016">Continue</span>
          <ChevronRight size={16} data-id="element-22017" />
        </button>
      </div>
    </div>;
};